import React from "react";
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import AppTableHeaderCell from "../../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTablePagenation from "../../../../../../views/components/common/pages/AppTablePagenation";
import AppTableRowCell from "../../../../../../views/components/common/pages/AppTableRowCell";
import { formatDate } from "../../../../../../views/components/common/functions/formatDate";
import { RedemptionTableColumn } from "../../../../../../models/Dashboard/Redemption/RedemptionDrilldown";
import {
  formatRedemptionCell,
  formatRedemptionCount,
  formatRedemptionShare,
} from "./redemptionFormat";

const CELL_FONT_PX = 11;
const CHIP_FONT_PX = 10;
const CELL_PADDING = "4px 8px";
const PAGE_SIZE_OPTIONS = [25, 50, 100];
/** Display-only: expiry shows the date with a fixed end-of-day time. */
const EXPIRY_DISPLAY_TIME = "23:50:00";

/** IDs stay plain digits — no locale commas (en-IN would turn 136232 into 1,36,232). */
const RAW_ID_KEYS = new Set([
  "coupon_id",
  "order_id",
  "product_id",
  "customer_id",
  "id",
]);

interface RedemptionDataTableProps {
  maxHeight: number;
  columns: RedemptionTableColumn[];
  rows: Array<Record<string, unknown>>;
  pageNo: number;
  limit: number;
  totalCount: number;
  sortableColumns: readonly string[];
  sortBy?: string;
  sortDir?: "asc" | "desc";
  onSort: (column: string) => void;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onPageSizeChange: (pageSize: number) => void;
}

const getColumnMinWidth = (column: RedemptionTableColumn): string => {
  try {
    if (column.type === "datetime") {
      return "160px";
    }
    if (column.key === "share_pct") {
      return "140px";
    }
    if (column.type === "number") {
      return "80px";
    }
    return "100px";
  } catch (error) {
    console.error(error);
    return "100px";
  }
};

const getSerialNumber = (page: number, pageSize: number, index: number): number => {
  try {
    return (Math.max(page, 1) - 1) * Math.max(pageSize, 1) + index + 1;
  } catch (error) {
    console.error(error);
    return index + 1;
  }
};

const rowKeyFor = (row: Record<string, unknown>, index: number): string => {
  try {
    const parts = [row.coupon_id, row.id, row.product_id, row.coupon_code, row.variant, row.city, index];
    return parts.map((part) => String(part ?? "")).join("-");
  } catch (error) {
    console.error(error);
    return `row-${index}`;
  }
};

const ShareCell = ({ value }: { value: unknown }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const share = typeof value === "number" ? value : Number(value);
  const width = Number.isFinite(share) ? Math.max(0, Math.min(100, share)) : 0;

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 6,
          borderRadius: 999,
          overflow: "hidden",
          bgcolor: colors.greyBackground,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: `${width}%`,
            height: "100%",
            borderRadius: 999,
            bgcolor: colors.purple,
          }}
        />
      </Box>
      <Box component="span" sx={{ fontWeight: 600 }}>
        {formatRedemptionShare(Number.isFinite(share) ? share : null)}
      </Box>
    </Box>
  );
};

const StatusChip = ({ value }: { value: unknown }) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const raw = value == null || value === "" ? "" : String(value);

  if (!raw) {
    return <>-</>;
  }

  const tone =
    raw === "redeemed"
      ? { fg: colors.successGreen, bg: colors.successGreenLightBackground }
      : raw === "expired"
        ? { fg: colors.errorRed, bg: theme.palette.blushPink.main }
        : raw === "pending"
          ? { fg: theme.palette.goldenYellow.main, bg: colors.lightOrange }
          : raw === "sent"
            ? { fg: colors.darkBlue, bg: colors.blueLightBackground }
            : { fg: colors.greyTextColor, bg: colors.veryLightGray };

  return (
    <Chip
      size="small"
      label={raw.charAt(0).toUpperCase() + raw.slice(1)}
      sx={{
        height: 18,
        fontSize: `${CHIP_FONT_PX}px`,
        fontWeight: 600,
        bgcolor: tone.bg,
        color: tone.fg,
        border: `1px solid ${tone.fg}33`,
      }}
    />
  );
};

const formatRawId = (value: unknown): string => {
  try {
    if (value == null || value === "") {
      return "-";
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(Math.trunc(value));
    }
    return String(value);
  } catch (error) {
    console.error(error);
    return "-";
  }
};

/** Uses the API's calendar date as-is (no timezone conversion) plus the fixed display time. */
const formatExpiryDate = (value: unknown): string => {
  try {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value ?? "").trim());
    if (!match) {
      return "-";
    }
    const [, year, month, day] = match;
    return `${day}/${month}/${year}, ${EXPIRY_DISPLAY_TIME}`;
  } catch (error) {
    console.error(error);
    return "-";
  }
};

const renderCell = (column: RedemptionTableColumn, value: unknown): React.ReactNode => {
  try {
    if (column.key === "share_pct") {
      return <ShareCell value={value} />;
    }
    if (column.key === "status") {
      return <StatusChip value={value} />;
    }
    if (RAW_ID_KEYS.has(column.key)) {
      return formatRawId(value);
    }
    if (column.key === "expiry_date") {
      return formatExpiryDate(value);
    }
    if (column.type === "datetime") {
      return formatDate(value, true) || "-";
    }
    if (column.type === "number") {
      if (typeof value === "number" && Number.isFinite(value)) {
        const formatted = formatRedemptionCount(value);
        return column.unit ? `${formatted}${column.unit}` : formatted;
      }
      return formatRedemptionCell(value);
    }
    return formatRedemptionCell(value);
  } catch (error) {
    console.error(error);
    return "-";
  }
};

const RedemptionDataTable: React.FC<RedemptionDataTableProps> = ({
  maxHeight,
  columns,
  rows,
  pageNo,
  limit,
  totalCount,
  sortableColumns,
  sortBy,
  sortDir = "desc",
  onSort,
  onPageChange,
  onPageSizeChange,
}) => {
  const theme = useTheme<Theme>();
  const headerColor = theme.palette.customColors.tableGrayHeadingColor;
  const sortable = new Set(
    sortableColumns.filter((key) => columns.some((column) => column.key === key)),
  );

  const renderSortIcon = (property: string) => {
    try {
      if (sortBy === property) {
        return sortDir === "asc" ? "↑" : "↓";
      }
      return "↑↓";
    } catch (error) {
      console.error(error);
      return "↑↓";
    }
  };

  const cellSx = {
    textAlign: "center" as const,
    fontSize: CELL_FONT_PX,
    padding: CELL_PADDING,
  };

  return (
    <Paper
      sx={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      <TableContainer sx={{ maxHeight }}>
        <Table stickyHeader>
          <TableHead sx={{ position: "relative", zIndex: 30 }}>
            <TableRow>
              <AppTableHeaderCell sx={{ ...cellSx, minWidth: "52px" }}>
                #
              </AppTableHeaderCell>
              {columns.map((column) => {
                const canSort = sortable.has(column.key);
                return (
                  <AppTableHeaderCell
                    key={column.key}
                    sx={{ ...cellSx, minWidth: getColumnMinWidth(column) }}
                  >
                    {canSort ? (
                      <TableSortLabel
                        active={sortBy === column.key}
                        direction={sortDir === "asc" ? "asc" : "desc"}
                        onClick={() => {
                          try {
                            onSort(column.key);
                          } catch (error) {
                            console.error(error);
                          }
                        }}
                        IconComponent={() => <span>{renderSortIcon(column.key)}</span>}
                        sx={{
                          fontSize: `${CELL_FONT_PX}px`,
                          color: headerColor,
                          "&.Mui-active": { color: headerColor },
                        }}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </AppTableHeaderCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const rowBg =
                index % 2 === 0
                  ? theme.palette.action.hover
                  : theme.palette.background.paper;
              return (
                <TableRow
                  key={rowKeyFor(row, index)}
                  hover
                  sx={{
                    backgroundColor: rowBg,
                    "&:hover": { backgroundColor: theme.palette.action.hover },
                  }}
                >
                  <AppTableRowCell sx={cellSx}>
                    {getSerialNumber(pageNo, limit, index)}
                  </AppTableRowCell>
                  {columns.map((column) => (
                    <AppTableRowCell key={`${column.key}-${index}`} sx={cellSx}>
                      {renderCell(column, row[column.key])}
                    </AppTableRowCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <AppTablePagenation
        fontSize={`${CELL_FONT_PX}px`}
        pageNo={pageNo}
        limit={limit}
        totalCount={totalCount}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        onPageChange={onPageChange}
        handleRowsChange={(value) => {
          try {
            onPageSizeChange(value);
          } catch (error) {
            console.error(error);
          }
        }}
      />
    </Paper>
  );
};

export default RedemptionDataTable;
